import React, { FC } from 'react';
import cs from 'classnames';
import { WithTranslation, withTranslation } from '@wix/wix-i18n-config';

import s from './infoPopUp.scss';
import { StoreTemplate, useTemplateContext } from '../../context/TemplateContext/TemplateContext';
import { useUIContext } from '../../context/UIContext/UIContext';
import { reportEditorClick } from '../../utils/bi';
import { infoPopUpDataHooks } from './InfoPopUp.dataHooks';
import { useExperimentContext } from '../../context/ExperimentContext/ExperimentContext';
import CloseIcon from './close-v2.svg';
import { CustomModal } from '../CustomModal';

interface EditButtonProps extends WithTranslation {
  dataHook?: string;
  template: StoreTemplate;
  editorClick: () => any;
  target?: string;
  className?: string;
}

const EditButton: FC<EditButtonProps> = ({ t, template, editorClick, target, className }) => (
  <a
    data-hook={infoPopUpDataHooks.cardEditorUrl}
    className={cs(s.editor, s.button, className)}
    {...(target && { target })}
    onClick={() => editorClick()}
    href={template.editorUrl}
  >
    {t('template.viewer.info.edit.button')}
  </a>
);

interface DesktopOnlyNoticeProps extends WithTranslation {}

function DesktopOnlyNotice({ t }: DesktopOnlyNoticeProps) {
  return (
    <div className={s.desktopOnlyNotice} data-hook={infoPopUpDataHooks.desktopOnlyNotice}>
      {t('template.viewer.info.desktop.only.notice')}
    </div>
  );
}

export interface InfoPopUpProps extends WithTranslation {
  dataHook?: string;
  isMobile?: boolean;
}

const InfoPopUpCmp: FC<InfoPopUpProps> = ({ t, i18n, isMobile, dataHook }) => {
  const { isInfoShown, setIsInfoShown } = useUIContext();
  const { template } = useTemplateContext();
  const { activeExperiments } = useExperimentContext();

  const isNewReadMoreModal = activeExperiments.includes('newReadMoreModal');

  const onEditorClick = () => {
    reportEditorClick({ origin: 'viewer_more' });
  };

  if (isNewReadMoreModal) {
    return (
      <CustomModal isOpen={isInfoShown} onClose={() => setIsInfoShown(false)}>
        <>
          <div className={s.modalContent}>
            <h1 className={s.modalTitle}>{t('template.viewer.info.title', { title: template.title })}</h1>
            <div className={s.modalFeature}>
              <h3 className={s.modalSubtitle}>{t('template.viewer.info.goodFor')}</h3>
              <p className={s.modalRunningText}>{template.goodFor}</p>
            </div>
            <div className={s.modalFeature}>
              <h3 className={s.modalSubtitle}>{t('template.viewer.info.description')}</h3>
              <p className={s.modalRunningText}>{template.description}</p>
            </div>
            {isMobile ? (
              <>
                <hr className={s.modalDivider} />
                <span className={s.modalMobileDescription}> {t('template.viewer.info.desktop.only.notice')}</span>
              </>
            ) : (
              <a
                href={template.editorUrl}
                className={s.modalBtn}
                target="_blank"
                onClick={onEditorClick}
                rel="noreferrer"
              >
                {t('template.viewer.info.edit.button')}
              </a>
            )}
          </div>
        </>
      </CustomModal>
    );
  }

  return (
    <div data-hook={dataHook} className={cs(s.popupWrapper, { [s.show]: isInfoShown }, s.newPopupWrapper)}>
      <div className={cs(s.card, s.newCardUi)}>
        {!isMobile && (
          <button
            data-hook={infoPopUpDataHooks.cardClose}
            className={s.newCloseButton}
            onClick={() => setIsInfoShown(!isInfoShown)}
          >
            <CloseIcon className={s.closeIcon} />
            <span className={s.hide}>{t('a11y.close.popup.button')}</span>
          </button>
        )}
        <div className={s.content}>
          <h1 data-hook={infoPopUpDataHooks.cardTitle} className={cs(s.title, s.newTitleUi)}>
            {t('template.viewer.info.title', { title: template.title })}
          </h1>
          <div className={cs(s.feature, s.newFeatureDescription)}>
            <h3 data-hook={infoPopUpDataHooks.cardGoodForTitle} className={s.subtitle}>
              {t('template.viewer.info.goodFor')}
            </h3>
            <p data-hook={infoPopUpDataHooks.cardGoodFor} className={s.runningText}>
              {template.goodFor}
            </p>
          </div>
          <div className={cs(s.feature, s.newDescription)}>
            <h3 className={s.subtitle}>{t('template.viewer.info.description')}</h3>
            <p data-hook={infoPopUpDataHooks.cardDescription} className={s.runningText}>
              {template.description}
            </p>
          </div>
        </div>
        <div className={s.footer}>
          {isMobile ? (
            <DesktopOnlyNotice t={t} i18n={i18n} tReady />
          ) : (
            <EditButton
              i18n={i18n}
              tReady
              target="_blank"
              className={s.newBtn}
              t={t}
              template={template}
              editorClick={onEditorClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const InfoPopUp = withTranslation()(InfoPopUpCmp);
